// @core/stores/activity/ActivityStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { activityApi } from '@core/api';
import type {
  Activity,
  ListActivitiesResponse,
} from '@core/types/activity';
import type { PageInfo } from '@core/types/pagination';

class ActivityStore {
  // List state
  items: Activity[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Activity | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Activity>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Activity | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Activity | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Get activity by ID
   */
  async GetActivity(id: string): Promise<Activity> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await activityApi.GetActivity(id);
      const item = response.activity;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List activities by scope
   */
  async ListActivities(params?: { category?: string | number | boolean; cursor?: string | number | boolean; direction?: string | number | boolean; limit?: string | number | boolean; object_id?: string | number | boolean; scope?: string | number | boolean; scope_id?: string | number | boolean; sort?: string | number | boolean; }): Promise<ListActivitiesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await activityApi.ListActivities(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const activityStore = new ActivityStore();
